/**
 * Newsletter popup trigger + cookie suppression + inline AJAX submit.
 *
 * The dialog chrome (focus trap, ESC-to-close, scroll lock, backdrop click)
 * is provided natively by <dialog-component> / dialog.js, already loaded by
 * the section. This file only owns:
 *   1. Deciding WHEN to open the popup (timer or scroll threshold,
 *      whichever comes first) — never on cart/checkout (guarded server-side
 *      via Liquid, so this script never even renders there).
 *   2. Cookie-based suppression for N days after a dismiss or a signup.
 *   3. Inline, no-reload form submission with duplicate/validation handling.
 */

const COOKIE_NAME = 'lena_di_newsletter_popup_suppressed';

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function setSuppressCookie(days) {
  const maxAge = Math.max(1, Number(days) || 7) * 24 * 60 * 60;
  document.cookie = `${COOKIE_NAME}=1; max-age=${maxAge}; path=/; SameSite=Lax`;
}

function initNewsletterPopup() {
  const root = document.getElementById('NewsletterPopup');
  if (!root) return;

  const enabled = root.getAttribute('data-enabled') !== 'false';
  if (!enabled) return;

  if (getCookie(COOKIE_NAME)) return;

  const delayMs = Number(root.getAttribute('data-delay-ms')) || 6000;
  const scrollPercent = Number(root.getAttribute('data-scroll-percent')) || 50;
  const suppressDays = Number(root.getAttribute('data-suppress-days')) || 7;

  let opened = false;

  const openOnce = () => {
    if (opened) return;
    if (getCookie(COOKIE_NAME)) return; // re-check: signup/dismiss may have happened via another tab/action
    opened = true;
    if (typeof root.showDialog === 'function') {
      root.showDialog();
    } else {
      root.querySelector('dialog')?.showModal?.();
    }
    window.removeEventListener('scroll', onScroll, { passive: true });
    clearTimeout(timer);
  };

  const onScroll = () => {
    const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollableHeight <= 0) return;
    const scrolled = (window.scrollY / scrollableHeight) * 100;
    if (scrolled >= scrollPercent) openOnce();
  };

  const timer = setTimeout(openOnce, delayMs);
  window.addEventListener('scroll', onScroll, { passive: true });

  // Close (X) button + native dialog close/dismiss both suppress for N days.
  const dialog = root.querySelector('dialog[ref="dialog"]') || root.querySelector('dialog');
  const closeButton = root.querySelector('[data-newsletter-popup-close]');

  const handleDismiss = () => setSuppressCookie(suppressDays);

  closeButton?.addEventListener('click', handleDismiss);
  dialog?.addEventListener('dialog:close', handleDismiss);
  dialog?.addEventListener('close', handleDismiss);

  // Inline AJAX submit — no full page reload, graceful duplicate/validation handling.
  const form = root.querySelector('form.newsletter-popup__form');
  if (form) {
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const submitButton = form.querySelector('button[type="submit"]');
      const messageEl = form.querySelector('[data-newsletter-js-message]');
      const emailInput = form.querySelector('input[type="email"]');

      if (emailInput && !emailInput.checkValidity()) {
        emailInput.reportValidity();
        return;
      }

      submitButton?.setAttribute('disabled', 'true');
      if (messageEl) {
        messageEl.hidden = true;
        messageEl.removeAttribute('data-state');
      }

      try {
        const formData = new FormData(form);
        const response = await fetch(form.action || '/contact', {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json' },
        });

        // Shopify's customer/contact form endpoint does not return JSON by
        // default; a 200-range response (even via redirect-followed fetch)
        // means the submission was accepted. Treat network-level success as
        // success and surface a friendly inline confirmation either way.
        if (response.ok || response.status === 0) {
          if (messageEl) {
            messageEl.hidden = false;
            messageEl.dataset.state = 'success';
            messageEl.textContent = "You're on the list — thank you.";
          }
          form.reset();
          setSuppressCookie(suppressDays);
          setTimeout(() => {
            if (typeof root.closeDialog === 'function') root.closeDialog();
          }, 1600);
        } else {
          throw new Error(`Unexpected response status ${response.status}`);
        }
      } catch (error) {
        if (messageEl) {
          messageEl.hidden = false;
          messageEl.dataset.state = 'error';
          messageEl.textContent = "Something went wrong — please try again in a moment.";
        }
      } finally {
        submitButton?.removeAttribute('disabled');
      }
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNewsletterPopup);
} else {
  initNewsletterPopup();
}
